import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { CartaSolicitud } from '../mocks/cartas';

const mapFromRow = (row: Record<string, unknown>): CartaSolicitud => ({
  id: String(row.id ?? ''),
  proyectoCodigo: String(row.proyecto_codigo ?? ''),
  proyectoTitulo: String(row.proyecto_titulo ?? ''),
  investigadorNombre: String(row.investigador_nombre ?? ''),
  investigadorEmail: String(row.investigador_email ?? ''),
  investigadorCargo: String(row.investigador_cargo ?? ''),
  investigadorUnidad: String(row.investigador_unidad ?? ''),
  ciudad: String(row.ciudad ?? ''),
  fecha: String(row.fecha ?? ''),
  cuerpo: String(row.cuerpo ?? ''),
  firma: row.firma_tipo && row.firma_valor
    ? { tipo: row.firma_tipo as 'texto' | 'imagen', valor: String(row.firma_valor) }
    : null,
  estado: (row.estado ? String(row.estado) as 'borrador' | 'enviada' | 'leida' : 'borrador'),
  fechaEnvio: row.fecha_envio ? String(row.fecha_envio) : null,
  notificadoComite: Boolean(row.notificado_comite ?? false),
});

const mapToRow = (c: CartaSolicitud): Record<string, unknown> => ({
  proyecto_codigo: c.proyectoCodigo,
  proyecto_titulo: c.proyectoTitulo,
  investigador_nombre: c.investigadorNombre,
  investigador_email: c.investigadorEmail,
  investigador_cargo: c.investigadorCargo,
  investigador_unidad: c.investigadorUnidad,
  ciudad: c.ciudad,
  fecha: c.fecha,
  cuerpo: c.cuerpo,
  firma_tipo: c.firma?.tipo ?? null,
  firma_valor: c.firma?.valor ?? null,
  estado: c.estado,
  fecha_envio: c.fechaEnvio,
  notificado_comite: c.notificadoComite,
});

export function useSupabaseCartas() {
  const [cartas, setCartas] = useState<CartaSolicitud[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCartas = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('ugpp_cartas')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) {
      console.error('Error cargando cartas:', error);
      setLoading(false);
      return;
    }
    setCartas((data || []).map(mapFromRow));
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCartas();
  }, [fetchCartas]);

  const addCarta = useCallback(async (carta: CartaSolicitud): Promise<boolean> => {
    const { error } = await supabase.from('ugpp_cartas').insert(mapToRow(carta));
    if (error) {
      console.error('Error insertando carta:', error);
      return false;
    }
    await fetchCartas();
    return true;
  }, [fetchCartas]);

  const updateCarta = useCallback(async (id: string, updates: Partial<CartaSolicitud>): Promise<boolean> => {
    const row: Record<string, unknown> = {};
    if (updates.estado !== undefined) row.estado = updates.estado;
    if (updates.fechaEnvio !== undefined) row.fecha_envio = updates.fechaEnvio;
    if (updates.notificadoComite !== undefined) row.notificado_comite = updates.notificadoComite;
    if (updates.firma !== undefined) {
      row.firma_tipo = updates.firma?.tipo ?? null;
      row.firma_valor = updates.firma?.valor ?? null;
    }

    if (Object.keys(row).length === 0) return true;

    const { error } = await supabase
      .from('ugpp_cartas')
      .update(row)
      .eq('id', id);

    if (error) {
      console.error('Error actualizando carta:', error);
      return false;
    }
    await fetchCartas();
    return true;
  }, [fetchCartas]);

  const marcarLeida = useCallback(async (id: string): Promise<boolean> => {
    return updateCarta(id, { estado: 'leida' });
  }, [updateCarta]);

  const getCartasByEmail = useCallback((email: string): CartaSolicitud[] => {
    return cartas.filter(c => c.investigadorEmail.toLowerCase() === email.toLowerCase());
  }, [cartas]);

  const getCartasByProyecto = useCallback((codigo: string): CartaSolicitud[] => {
    return cartas.filter(c => c.proyectoCodigo === codigo);
  }, [cartas]);

  return {
    cartas,
    loading,
    addCarta,
    updateCarta,
    marcarLeida,
    getCartasByEmail,
    getCartasByProyecto,
    refresh: fetchCartas,
  };
}